import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Plus, ShieldCheck, TrendingUp, Download, QrCode } from "lucide-react";
import api, { apiError, API } from "../../api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../auth";
import { Button, Card, Badge, Spinner, Modal, Textarea, Field, Select, Input, EmptyState, cx } from "../../components/ui";
import { toast } from "sonner";

const CONSENT_TYPES = ["utilisation_spinago", "acces_portail", "communication_electronique", "utilisation_courriel",
  "utilisation_messagerie", "utilisation_images_videos", "partage_cabinet", "politique_confidentialite", "conditions_utilisation"];

export default function PatientDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("overview");
  const canClinical = ["professional", "clinic_admin", "superadmin"].includes(user.role);

  useEffect(() => { api.get(`/patients/${id}`).then((r) => setP(r.data)).catch(() => navigate("/app/patients")); }, [id]);
  if (!p) return <div className="flex justify-center py-20"><Spinner /></div>;

  const tabs = [["overview", t("clinical_context")], canClinical && ["notes", t("notes")], ["consents", t("consents")],
    ["programs", t("programs")], canClinical && ["tracking", t("tracking")]].filter(Boolean);

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/app/patients")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft size={16} className="rtl:rotate-180" /> {t("patients")}</button>
      <Card className="p-5 flex flex-wrap items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-teal-50 text-teal flex items-center justify-center font-head font-bold text-lg">
          {p.first_name[0]}{p.last_name[0]}
        </div>
        <div>
          <h1 className="font-head font-bold text-navy text-xl">{p.first_name} {p.last_name}</h1>
          <div className="text-sm text-slate-400">{p.internal_number} · {p.phone || "—"} · {p.email || "—"}</div>
        </div>
        {canClinical && <Button className="ms-auto" onClick={() => navigate(`/app/programmes?patient=${id}`)} data-testid="create-program-for-patient"><Plus size={16} /> {t("build_program")}</Button>}
      </Card>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={cx("px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition",
              tab === k ? "border-teal text-teal" : "border-transparent text-slate-500 hover:text-navy")}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview p={p} canClinical={canClinical} t={t} />}
      {tab === "notes" && canClinical && <Notes id={id} t={t} />}
      {tab === "consents" && <Consents id={id} t={t} />}
      {tab === "programs" && <ProgramsTab id={id} t={t} canClinical={canClinical} />}
      {tab === "tracking" && canClinical && <Tracking id={id} t={t} />}
    </div>
  );
}

function Row({ label, value }) {
  return <div className="py-2 border-b border-slate-50 last:border-0"><div className="text-xs text-slate-400">{label}</div><div className="text-sm text-slate-700 mt-0.5">{value || "—"}</div></div>;
}

function Overview({ p, canClinical, t }) {
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="p-5">
        <h3 className="font-head font-semibold text-navy mb-2">{t("identity")}</h3>
        <Row label={t("birth_date")} value={p.birth_date} />
        <Row label={t("region")} value={p.region} />
        <Row label={t("address")} value={p.address} />
        <Row label={t("lang")} value={p.lang} />
        <Row label="Contact d'urgence" value={`${p.emergency_contact || ""} ${p.emergency_phone || ""}`} />
      </Card>
      {canClinical && (
        <Card className="p-5">
          <h3 className="font-head font-semibold text-navy mb-2">{t("clinical_context")}</h3>
          <Row label="Région anatomique" value={p.body_region} />
          <Row label={t("reason")} value={p.reason} />
          <Row label={t("diagnosis")} value={p.diagnosis} />
          <Row label="Douleur initiale" value={p.initial_pain != null ? `${p.initial_pain}/10` : "—"} />
          <Row label={t("precautions")} value={p.precautions} />
          <Row label="Objectifs fonctionnels" value={p.functional_goals} />
        </Card>
      )}
    </div>
  );
}

function Notes({ id, t }) {
  const [notes, setNotes] = useState(null);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const load = () => api.get(`/patients/${id}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  useEffect(() => { load(); }, [id]);
  const add = async () => {
    if (!content.trim()) return;
    try { await api.post(`/patients/${id}/notes`, { content, note_type: "suivi", locked: true }); toast.success("Note ajoutée"); setContent(""); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <Button onClick={() => setOpen(true)} data-testid="add-note-btn"><Plus size={16} /> {t("add_note")}</Button>
      {!notes ? <Spinner /> : notes.length === 0 ? <EmptyState title={t("no_data")} /> :
        notes.map((n) => (
          <Card key={n.id} className="p-4">
            <div className="flex items-center gap-2 mb-1"><Badge tone="navy">{n.note_type}</Badge>{n.locked && <Badge tone="green">verrouillée</Badge>}
              <span className="ms-auto text-xs text-slate-400">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span></div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.content}</p>
            <div className="text-xs text-slate-400 mt-2">— {n.author_name}</div>
          </Card>
        ))}
      <Modal open={open} onClose={() => setOpen(false)} title={t("add_note")}>
        <Field label={t("note_content")}><Textarea rows={5} value={content} onChange={(e) => setContent(e.target.value)} data-testid="note-content" /></Field>
        <div className="flex justify-end gap-2 mt-4"><Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button onClick={add} data-testid="note-submit">{t("save")}</Button></div>
      </Modal>
    </div>
  );
}

function Consents({ id, t }) {
  const [items, setItems] = useState(null);
  const load = () => api.get(`/patients/${id}/consents`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [id]);
  const toggle = async (type, granted) => {
    try { await api.post(`/patients/${id}/consents`, { consent_type: type, granted, version: "1.0" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const latest = (type) => (items || []).filter((c) => c.consent_type === type).sort((a, b) => (b.created_at > a.created_at ? 1 : -1))[0];
  if (!items) return <Spinner />;
  return (
    <Card className="divide-y divide-slate-100">
      {CONSENT_TYPES.map((type) => {
        const c = latest(type);
        return (
          <div key={type} className="flex items-center gap-3 px-4 py-3">
            <ShieldCheck size={18} className={c?.granted ? "text-teal" : "text-slate-300"} />
            <span className="text-sm text-slate-700 capitalize">{type.replace(/_/g, " ")}</span>
            {c && <Badge tone={c.granted ? "green" : "default"}>{c.granted ? "Accordé" : "Retiré"}</Badge>}
            <div className="ms-auto flex gap-2">
              <Button size="sm" variant={c?.granted ? "ghost" : "primary"} onClick={() => toggle(type, true)} data-testid={`consent-grant-${type}`}>Accorder</Button>
              <Button size="sm" variant="ghost" onClick={() => toggle(type, false)}>Retirer</Button>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

function ProgramsTab({ id, t, canClinical }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [qr, setQr] = useState(null);
  useEffect(() => { api.get(`/programs?patient_id=${id}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [id]);
  const pdf = (gid) => window.open(`${API}/programs/${gid}/pdf?token=`, "_blank"); // fallback handled below
  const downloadPdf = async (gid, lang) => {
    try {
      const res = await api.get(`/programs/${gid}/pdf?lang=${lang}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `programme-${gid.slice(0, 8)}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erreur PDF"); }
  };
  if (!items) return <Spinner />;
  if (items.length === 0) return <EmptyState title={t("no_program")} />;
  return (
    <div className="space-y-3">
      {items.map((g) => (
        <Card key={g.id} className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <div className="font-medium text-navy">{g.goal || "Programme"} <span className="text-xs text-slate-400">v{g.version}</span></div>
              <div className="text-xs text-slate-400">{(g.items || []).length} exercices · {t("reeval_date")}: {g.reeval_date || "—"}</div>
            </div>
            <Badge tone={g.status === "actif" ? "green" : "navy"} className="ms-2">{g.status}</Badge>
            <div className="ms-auto flex gap-2 flex-wrap">
              {canClinical && <Button size="sm" variant="outline" onClick={() => navigate(`/app/programmes?edit=${g.id}`)}>{t("edit")}</Button>}
              <Button size="sm" variant="outline" onClick={() => downloadPdf(g.id, "fr")} data-testid={`pdf-${g.id}`}><Download size={14} /> PDF</Button>
              <Button size="sm" variant="ghost" onClick={() => downloadPdf(g.id, "ar")}>PDF ع</Button>
              <Button size="sm" variant="ghost" onClick={() => setQr(`${API}/programs/${g.id}/qr`)} data-testid={`qr-${g.id}`}><QrCode size={14} /></Button>
            </div>
          </div>
        </Card>
      ))}
      <Modal open={!!qr} onClose={() => setQr(null)} title={t("show_qr")} size="sm">
        <div className="flex flex-col items-center gap-3">
          {qr && <QrImage src={qr} />}
          <p className="text-xs text-slate-400 text-center">Le code QR ouvre une page sécurisée par jeton temporaire. Aucune donnée médicale n'est stockée dans le code.</p>
        </div>
      </Modal>
    </div>
  );
}

function QrImage({ src }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { api.get(src.replace(API, ""), { responseType: "blob" }).then((r) => setUrl(URL.createObjectURL(r.data))); }, [src]);
  return url ? <img src={url} alt="QR" className="h-52 w-52" /> : <Spinner />;
}

function Tracking({ id, t }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/patients/${id}/tracking`).then((r) => setData(r.data)).catch(() => setData({ completions: [], feedback: [], summary: {} })); }, [id]);
  if (!data) return <Spinner />;
  const s = data.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4"><div className="text-2xl font-bold font-head text-teal">{s.adherence || 0}%</div><div className="text-xs text-slate-400">{t("adherence")}</div></Card>
        <Card className="p-4"><div className="text-2xl font-bold font-head text-navy">{s.completed || 0}</div><div className="text-xs text-slate-400">Réalisés</div></Card>
        <Card className="p-4"><div className="text-2xl font-bold font-head text-alert">{s.high_pain_count || 0}</div><div className="text-xs text-slate-400">{t("high_pain_alert")}</div></Card>
        <Card className="p-4"><div className="text-2xl font-bold font-head text-warn">{s.high_difficulty_count || 0}</div><div className="text-xs text-slate-400">Difficulté élevée</div></Card>
      </div>
      <Card className="p-5">
        <h3 className="font-head font-semibold text-navy mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-teal" /> Dernières activités</h3>
        {(data.completions || []).length === 0 ? <EmptyState title={t("no_data")} /> : (
          <div className="divide-y divide-slate-100">
            {data.completions.slice(0, 12).map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2 text-sm">
                <Badge tone={c.completed ? "green" : "default"}>{c.completed ? t("done") : "—"}</Badge>
                <span className="text-slate-500">Douleur {c.pain_before}→<b className={c.pain_after >= 7 ? "text-alert" : "text-slate-700"}>{c.pain_after}</b></span>
                <span className="text-slate-400">Diff. {c.difficulty}/10</span>
                {c.comment && <span className="text-slate-500 italic truncate">"{c.comment}"</span>}
                <span className="ms-auto text-xs text-slate-400">{(c.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
